import React, { useState, useEffect, useRef } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  message, 
  Upload, 
  Space, 
  Select,
  DatePicker,
  Spin,
  Divider,
  Row,
  Col,
  Tabs
} from 'antd';
import { 
  UploadOutlined, 
  SaveOutlined, 
  PhoneOutlined,
  UserOutlined,
  DollarOutlined,
  CameraOutlined,
  InboxOutlined,
  CarOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { submitShipment } from '../services/shipmentService';
import { searchCustomers } from '../services/customerService';
import { compressImage } from '../utils/imageUtils';
import debounce from 'lodash/debounce';
import locale from 'antd/es/date-picker/locale/zh_CN';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Submit = () => {
  const { currentUser } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // 分类存储不同类型的图片
  const [cargoImages, setCargoImages] = useState([]);
  const [packingImages, setPackingImages] = useState([]);
  const [shippingImages, setShippingImages] = useState([]);
  
  // 当前激活的图片上传标签页
  const [activeImageTab, setActiveImageTab] = useState('cargo');
  
  // 创建ref来引用上传组件
  const uploadRefs = {
    cargo: useRef(),
    packing: useRef(),
    shipping: useRef()
  };

  // 监听粘贴事件
  useEffect(() => {
    const handlePaste = async (event) => {
      // 检查是否有图片数据
      if (!event.clipboardData || !event.clipboardData.items) return;
      
      // 查找粘贴的图片
      const items = Array.from(event.clipboardData.items);
      const imageItem = items.find(item => item.type.startsWith('image/'));
      
      if (imageItem) {
        // 阻止默认粘贴行为
        event.preventDefault();
        
        // 获取图片文件
        const file = imageItem.getAsFile();
        if (!file) return;
        
        // 根据当前激活的标签页决定上传到哪个分类
        try {
          message.loading('正在处理粘贴的图片...', 0.5);
          
          // 压缩图片
          const compressedFile = await compressImage(file);
          
          // 显示压缩结果
          const beforeSize = (file.size / 1024 / 1024).toFixed(2);
          const afterSize = (compressedFile.size / 1024 / 1024).toFixed(2);
          message.success(`图片已压缩: ${beforeSize}MB → ${afterSize}MB`);
          
          // 创建上传文件对象
          const uploadFile = {
            uid: Date.now(),
            name: `paste-${Date.now()}.${file.type.split('/')[1] || 'png'}`,
            status: 'done',
            url: URL.createObjectURL(compressedFile),
            originFileObj: compressedFile
          };
          
          // 根据当前标签页更新对应的图片列表
          switch (activeImageTab) {
            case 'cargo':
              setCargoImages(prev => [...prev, uploadFile]);
              break;
            case 'packing':
              setPackingImages(prev => [...prev, uploadFile]);
              break;
            case 'shipping':
              setShippingImages(prev => [...prev, uploadFile]);
              break;
            default:
              setCargoImages(prev => [...prev, uploadFile]);
          }
          
        } catch (error) {
          console.error('处理粘贴图片失败:', error);
          message.error('处理粘贴图片失败');
        }
      }
    };
    
    // 添加事件监听
    document.addEventListener('paste', handlePaste);
    
    // 清除事件监听
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [activeImageTab]); // 依赖当前激活的标签页

  // 当选择客户时自动填充客户信息
  const handleCustomerSelect = (value, option) => {
    if (option && option.data) {
      const customer = option.data;
      form.setFieldsValue({
        name: customer.name,
        phone: customer.phone,
        address: customer.address
      });
    }
  };
  
  // 搜索客户（防抖）
  const searchCustomer = debounce(async (value) => {
    if (!value || value.length < 2) return;
    
    setSearchLoading(true);
    try {
      const data = await searchCustomers(value);
      setCustomers(data);
    } catch (error) {
      console.error('搜索客户失败:', error);
    } finally {
      setSearchLoading(false);
    }
  }, 500);
  
  // 图片上传前处理（压缩）- 货物照片
  const beforeUploadCargo = async (file, fileList) => {
    console.log(`货物照片上传: ${file.name}, 共${fileList.length}个文件`);
    return handleImageUpload(file, cargoImages, setCargoImages);
  };

  // 图片上传前处理（压缩）- 打包照片
  const beforeUploadPacking = async (file, fileList) => {
    console.log(`打包照片上传: ${file.name}, 共${fileList.length}个文件`);
    return handleImageUpload(file, packingImages, setPackingImages);
  };

  // 图片上传前处理（压缩）- 物流照片
  const beforeUploadShipping = async (file, fileList) => {
    console.log(`物流照片上传: ${file.name}, 共${fileList.length}个文件`);
    return handleImageUpload(file, shippingImages, setShippingImages);
  };
  
  // 通用图片处理函数
  const handleImageUpload = async (file, currentList, setListFunction) => {
    // 生成唯一ID
    const uniqueId = `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    console.log(`处理图片: ${file.name}, ID: ${uniqueId}`);
    
    // 验证文件类型
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
      return Upload.LIST_IGNORE;
    }
    
    // 验证文件大小
    if (file.size > 100 * 1024 * 1024) {
      message.error('图片大小不能超过100MB');
      return Upload.LIST_IGNORE;
    }
    
    // 验证文件格式
    const isJpgOrPng = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    if (!isJpgOrPng) {
      message.error('只支持JPG、PNG和WebP格式的图片');
      return Upload.LIST_IGNORE;
    }
    
    // 压缩图片
    try {
      message.loading(`处理图片: ${file.name}`, 0.5);
      const compressedFile = await compressImage(file);
      
      // 显示压缩结果
      const beforeSize = (file.size / 1024 / 1024).toFixed(2);
      const afterSize = (compressedFile.size / 1024 / 1024).toFixed(2);
      message.success(`图片已压缩: ${beforeSize}MB → ${afterSize}MB`);
      
      // 使用函数式更新来确保获取最新的状态
      setListFunction(prevList => {
        // 检查是否已存在该图片
        const exists = prevList.some(item => 
          item.name === file.name && 
          item.size === file.size
        );
        
        if (exists) {
          console.log(`图片已存在于列表中: ${file.name}`);
          return prevList;
        }
        
        // 返回新列表
        return [...prevList, {
          uid: uniqueId,
          name: file.name,
          status: 'done',
          url: URL.createObjectURL(compressedFile),
          originFileObj: compressedFile,
          size: file.size
        }];
      });
      
      // 阻止自动上传
      return false;
    } catch (error) {
      console.error('图片处理失败:', error);
      message.error('图片处理失败');
      return Upload.LIST_IGNORE;
    }
  };
  
  // 移除图片 - 货物照片
  const handleRemoveCargo = (file) => {
    const newFileList = cargoImages.filter(item => item.uid !== file.uid);
    setCargoImages(newFileList);
  };
  
  // 移除图片 - 打包照片
  const handleRemovePacking = (file) => {
    const newFileList = packingImages.filter(item => item.uid !== file.uid);
    setPackingImages(newFileList);
  };
  
  // 移除图片 - 物流照片
  const handleRemoveShipping = (file) => {
    const newFileList = shippingImages.filter(item => item.uid !== file.uid);
    setShippingImages(newFileList);
  };
  
  // 提交表单
  const handleSubmit = async (values) => {
    setLoading(true);
    
    try {
      // 创建FormData对象
      const formData = new FormData();
      
      // 添加表单字段
      Object.keys(values).forEach(key => {
        if (key !== 'customerSearch' && values[key] !== undefined) {
          // 处理日期对象
          if (values[key] instanceof Date || (values[key] && values[key]._isAMomentObject)) {
            formData.append(key, values[key].format('YYYY-MM-DD'));
          } else {
            formData.append(key, values[key]);
          }
        }
      });
      
      // 添加不同类型的图片文件
      cargoImages.forEach(file => {
        if (file.originFileObj) {
          formData.append('cargo', file.originFileObj, file.name);
        }
      });
      
      packingImages.forEach(file => {
        if (file.originFileObj) {
          formData.append('packing', file.originFileObj, file.name);
        }
      });
      
      shippingImages.forEach(file => {
        if (file.originFileObj) {
          formData.append('shipping', file.originFileObj, file.name);
        }
      });
      
      // 添加操作人信息
      formData.append('operator', currentUser.username);
      
      // 提交数据
      const result = await submitShipment(formData);
      
      if (result.success) {
        message.success('运单提交成功');
        // 重置表单和图片列表
        form.resetFields();
        setCargoImages([]);
        setPackingImages([]);
        setShippingImages([]);
      } else {
        message.error(result.error || '提交失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error(error.message || '提交失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 处理标签页切换
  const handleTabChange = (key) => {
    setActiveImageTab(key);
  };
  
  return (
    <div className="submit-page">
      <Card className="submit-form">
        <Title level={4}>发货信息提交</Title>
        <Text type="secondary">请填写完整的发货信息并上传相关照片</Text>
        
        <Divider />
        
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              date: null,
              status: '已入库'
            }}
          >
            <div className="form-section">
              <h3><UserOutlined /> 基本信息</h3>
              
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="customerSearch"
                    label="搜索客户"
                  >
                    <Select
                      showSearch
                      placeholder="输入客户姓名或电话搜索"
                      filterOption={false}
                      onSearch={searchCustomer}
                      onChange={handleCustomerSelect}
                      loading={searchLoading}
                      notFoundContent={searchLoading ? <Spin size="small" /> : null}
                    >
                      {customers.map(customer => {
                        // 处理电话号码显示
                        const displayPhone = customer.phone?.startsWith('CUST-') ? '未提供电话' : customer.phone;
                        
                        return (
                          <Option key={customer.phone} value={customer.phone} data={customer}>
                            {customer.name} ({displayPhone})
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="date"
                    label="发货日期"
                    rules={[{ required: true, message: '请选择发货日期' }]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      locale={locale}
                      format="YYYY-MM-DD"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="name"
                    label="客户姓名"
                    rules={[{ required: true, message: '请输入客户姓名' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="客户姓名" maxLength={50} />
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label="联系电话"
                    rules={[{ required: false, message: '请输入联系电话或选择无电话' }]}
                    help="如果客户没有提供电话，可以留空或点击‘无电话’按钮"
                  >
                    <Input.Group compact>
                      <Button 
                        onClick={() => {
                          form.setFieldsValue({ phone: '' });
                          setTimeout(() => form.validateFields(['phone']), 100);
                        }}
                        style={{ width: '25%' }}
                      >
                        无电话
                      </Button>
                      <Input 
                        prefix={<PhoneOutlined />} 
                        placeholder="联系电话" 
                        style={{ width: '75%' }}
                      />
                    </Input.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="cost"
                    label="费用（元）"
                  >
                    <Input prefix={<DollarOutlined />} type="number" step="0.01" placeholder="请输入费用金额" />
                  </Form.Item>
                </Col>
              </Row>
            </div>
            
            <div className="form-section">
              <h3><CameraOutlined /> 图片上传</h3>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                支持直接粘贴截图或图片文件 (Ctrl+V)
              </Text>
              
              <Tabs defaultActiveKey="cargo" onChange={handleTabChange}>
                <Tabs.TabPane tab="货物照片" key="cargo">
                  <div className="upload-section">
                    <Upload
                      ref={uploadRefs.cargo}
                      listType="picture-card"
                      beforeUpload={beforeUploadCargo}
                      onRemove={handleRemoveCargo}
                      fileList={cargoImages}
                      multiple
                      accept="image/*"
                    >
                      <div>
                        <CameraOutlined style={{ fontSize: '24px' }} />
                        <div style={{ marginTop: 8 }}>点击上传或粘贴图片</div>
                      </div>
                    </Upload>
                    <div className="upload-hint">请上传货物的清晰照片</div>
                  </div>
                </Tabs.TabPane>
                
                <Tabs.TabPane tab="打包过程照片" key="packing">
                  <div className="upload-section">
                    <Upload
                      ref={uploadRefs.packing}
                      listType="picture-card"
                      beforeUpload={beforeUploadPacking}
                      onRemove={handleRemovePacking}
                      fileList={packingImages}
                      multiple
                      accept="image/*"
                    >
                      <div>
                        <InboxOutlined style={{ fontSize: '24px' }} />
                        <div style={{ marginTop: 8 }}>点击上传或粘贴图片</div>
                      </div>
                    </Upload>
                    <div className="upload-hint">请上传打包过程的照片</div>
                  </div>
                </Tabs.TabPane>
                
                <Tabs.TabPane tab="物流凭证照片" key="shipping">
                  <div className="upload-section">
                    <Upload
                      ref={uploadRefs.shipping}
                      listType="picture-card"
                      beforeUpload={beforeUploadShipping}
                      onRemove={handleRemoveShipping}
                      fileList={shippingImages}
                      multiple
                      accept="image/*"
                    >
                      <div>
                        <CarOutlined style={{ fontSize: '24px' }} />
                        <div style={{ marginTop: 8 }}>点击上传或粘贴图片</div>
                      </div>
                    </Upload>
                    <div className="upload-hint">请上传物流凭证照片</div>
                  </div>
                </Tabs.TabPane>
              </Tabs>
            </div>
            
            <div className="form-section">
              <h3><InboxOutlined /> 货物信息</h3>
              
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="tracking_no"
                    label="快递单号"
                  >
                    <Input placeholder="快递单号（可选）" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="weight"
                    label="重量(kg)"
                  >
                    <Input type="number" placeholder="重量" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="quantity"
                    label="数量"
                  >
                    <Input type="number" placeholder="数量" />
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item
                name="content"
                label="货物内容"
                rules={[{ required: true, message: '请输入货物内容' }]}
              >
                <TextArea 
                  rows={3} 
                  placeholder="请描述货物内容" 
                  maxLength={200} 
                  showCount 
                />
              </Form.Item>
              
              <Form.Item
                name="notes"
                label="备注"
              >
                <TextArea 
                  rows={2} 
                  placeholder="备注信息（可选）" 
                  maxLength={200} 
                  showCount 
                />
              </Form.Item>
              
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select placeholder="选择状态">
                  <Option value="已入库">已入库</Option>
                  <Option value="已出库">已出库</Option>
                  <Option value="运输中">运输中</Option>
                  <Option value="已签收">已签收</Option>
                </Select>
              </Form.Item>
            </div>
            
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
                block
                className="submit-button"
              >
                提交运单信息
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default Submit;